/** The typed surface of the services the web app talks to. Shapes mirror the OpenAPI documents. */
import type { Deck } from "../cards/types";
import type { AppConfig } from "../config";
import type { PaperDoc } from "../paper/types";
import { apiFetch, type ApiResult } from "./client";

export type Me = {
  user_id: string;
  workspace_id: string;
  role: string;
  email: string;
  terms: { current: string; accepted: boolean };
};
export type Subject = {
  id: string;
  workspace_id: string;
  name: string;
  qualification: string;
  created_at: string;
};
export type DocumentStatus = "pending" | "uploaded" | "rejected" | "withdrawn";
export type Doc = {
  id: string;
  subject_id: string;
  kind: "textbook" | "past_paper";
  filename: string;
  s3_key: string;
  size_bytes: number;
  page_count: number | null;
  status: DocumentStatus;
  reject_reason: string | null;
  created_at: string;
  book_id: string | null;
  book_status: BookStatus | null;
  /** While confirmed or ingesting; absent from a content before design v2. */
  progress?: BookProgress | null;
  /** A past paper's own (API_CHANGES 14): what its first page says, and its paper. */
  role?: PastPaperRole | null;
  role_by?: "detected" | "student" | null;
  part?: number | null;
  series?: string | null;
  past_paper_id?: string | null;
  grouped_by?: "detected" | "student" | null;
  described_at?: string | null;
};
export type PastPaperRole = "question_paper" | "mark_scheme" | "insert";
/** One past paper: its question paper (in parts), mark scheme and insert. */
export type PastPaper = { id: string; name: string; series: string | null; documents: Doc[] };
export type PastPaperListing = { papers: PastPaper[]; unmatched: Doc[] };
/** The chapters this ingest run covers, and the estimate's own clock for the rest. */
export type BookProgress = { chapters_done: number; chapters_total: number; eta_seconds: number };
export type PresignedPost = { url: string; fields: Record<string, string> };
export type BookStatus = "detecting" | "review" | "confirmed" | "ingesting" | "ready" | "failed";
export type OffsetEvidenceWire = {
  method?: "outline" | "contents" | "headings" | "none";
  offset?: number | null;
  samples?: { pdf_page: number; printed: number | null; agrees: boolean }[];
  contents_pages?: number[];
  gaps?: { start_page: number; end_page: number; label: string }[];
  reason?: string | null;
  /** Why it was unsure, as a code (bug fix 1, spec 6); absent from a book detected before. */
  reason_code?: "offset_majority" | "uncalibrated" | "headings" | "low_confidence" | null;
  scope?: "book" | "chapter" | null;
};
export type Book = {
  id: string;
  workspace_id: string;
  subject_id: string;
  source_document_id: string;
  title: string;
  filename: string;
  page_count: number;
  page_offset: number | null;
  offset_evidence: OffsetEvidenceWire;
  status: BookStatus;
  /** While confirmed or ingesting (API_CHANGES 3); null otherwise, absent before design v2. */
  progress?: BookProgress | null;
  created_at: string;
  updated_at: string;
};
export type ChapterWire = {
  id: string;
  workspace_id: string;
  book_id: string;
  number: number;
  title: string;
  start_page: number;
  end_page: number;
  detection_confidence: number;
  detection_reason: string | null;
  confirmed_at: string | null;
  included: boolean;
  ingested_at: string | null;
  /** The start page's text, on a flagged row (confidence ≤ 0.75); null when never read. */
  excerpt?: string | null;
};
export type ChapterPut = {
  number: number;
  title: string;
  start_page: number;
  end_page: number;
  included: boolean;
};
/** A fix to a ready book's chapters (bug fix 1, spec 7): what it keeps, remakes and costs. */
export type RevisePlan = {
  keep: number[];
  remake: number[];
  billable_pages: number;
  seconds?: number;
  quota?: { allowed: boolean; remaining: number | null; reason?: string } | null;
};
export type Estimate = {
  chapters: number;
  pages: number;
  seconds: number;
  quota: {
    allowed: boolean;
    meter: string;
    remaining: number | null;
    reason?: "ok" | "over_cap" | "not_entitled";
  };
};
/** A chapter-list rule broken, and the likely correction when there is one (API_CHANGES 8). */
export type ChapterIssue = {
  chapter: number | null;
  message: string;
  fix?: { chapter: number; start_page?: number; end_page?: number } | null;
};
export type Meter = {
  name: string;
  limit: number;
  used: number;
  held: number;
  remaining: number;
  period_start: string;
  /** The first of next month; null for subjects, which never resets. */
  resets_on?: string | null;
};
export type Caps = { subjects: number; pages: number; papers: number };
export type EntitlementReason = "ok" | "never_subscribed" | "lapsed" | "code_expired";
export type PlanKind = "none" | "trial" | "paid" | "complimentary" | "read_only";
/** GET /billing/me: may this workspace spend, under which caps, and where each meter stands. */
export type Entitlement = {
  plan: string;
  status: string;
  active: boolean;
  reason: EntitlementReason;
  ends_at: string | null;
  caps: Caps;
  meters: Meter[];
  /** What the header and account page say, decided by billing (API_CHANGES 10). */
  kind: PlanKind;
  trial_days_left: number | null;
  /** False once a subscription is cancelled, even while it runs to its end. */
  renews: boolean;
  chosen_plan: "starter" | "full" | null;
};
export type CheckoutHandshake = {
  customer_id: string;
  price_id: string;
  plan: string;
  amount: number;
  currency: string;
  trial_days: number;
  promo: string | null;
};
export type Severity = "OK" | "WARN" | "GAP" | "NEEDS-REVIEW";
export type FindingWire = {
  chapter_number: number | null;
  chapter_title?: string | null;
  start_page?: number | null;
  end_page?: number | null;
  severity: Severity;
  locator: string;
  message: string;
};
/** Chapters ingest finished, each counted once under its most serious finding. */
export type CoverageSummary = {
  chapters_total: number;
  complete: number;
  worth_a_look: number;
  not_covered: number;
  could_not_read: number;
  pages_processed: number;
};
export type DeckDoc = {
  book_id: string;
  title: string;
  subject_name: string;
  prompt_version: string;
  card_count: number;
  decks: Deck[];
};

export type ProfileStatus = "draft" | "confirmed" | "failed";
export type QuestionTypeShare = { type: string; typical_marks: number; share: number };
export type Exemplar = {
  document_id: string;
  /** The past paper it came from (API_CHANGES 14); absent on a format learned before sets. */
  past_paper_id?: string | null;
  page: number;
  marks: number;
  text: string;
  /** False: not one of the shapes the writer is given, still checked for copying. */
  pattern?: boolean;
  /** The command word it asks with, computed by the server from the profile's words. */
  command?: string | null;
};
export type Profile = {
  id: string;
  workspace_id: string;
  subject_id: string;
  status: ProfileStatus;
  board: string;
  paper_title: string;
  command_words: string[];
  question_types: QuestionTypeShare[];
  total_marks: number;
  per_question_range: number[];
  duration_minutes: number;
  rubric_style: string;
  exemplars: Exemplar[];
  source_document_ids: string[];
  provenance: {
    model?: string;
    cost_usd?: number;
    chars_read?: number;
    learned_at?: string;
    notes?: string[];
  };
  error: string | null;
  created_at: string;
  updated_at: string;
};
export type ProfilePut = Pick<
  Profile,
  "board" | "paper_title" | "command_words" | "question_types" | "total_marks" | "duration_minutes"
> & {
  per_question_range?: number[];
  /** A subset of the current list: exemplars can be removed, never added. */
  exemplars?: Exemplar[];
};
export type PaperLength = "short" | "standard" | "full";
export type PaperStatus = "requested" | "generating" | "ready" | "failed";
/** The format a paper was written with, kept on its row (API_CHANGES 6); null on older rows. */
export type PaperFormat = {
  profile_id: string;
  board: string;
  paper_title: string;
  command_words: string[];
  question_types: QuestionTypeShare[];
  total_marks: number;
  per_question_range: number[];
  duration_minutes: number;
  target_marks: number;
  target_minutes: number;
};
export type PaperRow = {
  id: string;
  workspace_id: string;
  subject_id: string;
  book_id: string;
  profile_id: string;
  chapters: number[];
  length: PaperLength;
  status: PaperStatus;
  key: string | null;
  total_marks: number | null;
  question_count: number | null;
  sections_failed: number;
  execution_id: string | null;
  error: string | null;
  cost_usd: number | null;
  format: PaperFormat | null;
  /** Section progress (API_CHANGES item 4); absent from a content before design v2. */
  sections_total?: number | null;
  sections_done?: number;
  sections_used_draft?: number;
  /** "Paper N" within the subject; never reused (API_CHANGES 13). */
  number: number;
  /** The student's own name for it, beside the number. */
  name: string | null;
  /** The id's first eight hex digits, "67F1-9C81": printed on every page. */
  ref: string;
  created_at: string;
  updated_at: string;
};
export type PaperRequest = { book_id: string; chapters: number[]; length: PaperLength };

/** POST /content/feedback (spec §7.2). `rating` or `body` — at least one, the service checks. */
export type FeedbackBody = {
  kind: "paper" | "deck" | "general";
  ref_id?: string;
  rating?: 1 | -1;
  body?: string;
  path?: string;
  broke?: boolean;
};

export type Api = {
  me(): Promise<ApiResult<Me>>;
  acceptTerms(version: string): Promise<ApiResult<undefined>>;
  listSubjects(): Promise<ApiResult<{ subjects: Subject[] }>>;
  createSubject(name: string, qualification: string): Promise<ApiResult<Subject>>;
  listDocuments(subjectId: string): Promise<ApiResult<{ documents: Doc[] }>>;
  createDocument(input: {
    subject_id: string;
    kind: Doc["kind"];
    filename: string;
    size_bytes: number;
    /** "Add a file" to a past paper: placed there, with the role chosen. */
    past_paper_id?: string;
    role?: PastPaperRole;
  }): Promise<ApiResult<{ document: Doc; upload: PresignedPost }>>;
  getDocument(id: string): Promise<ApiResult<Doc>>;
  listPastPapers(subjectId: string): Promise<ApiResult<PastPaperListing>>;
  /** Move a file to a paper (or "new"), or say what it is; answers the new listing. */
  placeDocument(
    id: string,
    body: { past_paper_id?: string; role?: PastPaperRole },
  ): Promise<ApiResult<PastPaperListing>>;
  renamePastPaper(id: string, name: string): Promise<ApiResult<PastPaperListing>>;
  /** The paper and every file in it. */
  deletePastPaper(id: string): Promise<ApiResult<undefined>>;
  deleteDocument(id: string): Promise<ApiResult<undefined>>;
  /** The subject and everything in it: documents, books, the format and its papers. */
  deleteSubject(id: string): Promise<ApiResult<undefined>>;
  getBook(id: string): Promise<ApiResult<Book>>;
  listChapters(bookId: string): Promise<ApiResult<{ chapters: ChapterWire[] }>>;
  putChapters(
    bookId: string,
    chapters: ChapterPut[],
  ): Promise<ApiResult<{ chapters: ChapterWire[] }>>;
  getExcerpt(
    documentId: string,
    page: number,
  ): Promise<ApiResult<{ page: number; excerpt: string }>>;
  /** A ~200 px render of one page; the url is presigned and short-lived (API_CHANGES 8). */
  getPreview(documentId: string, page: number): Promise<ApiResult<{ page: number; url: string }>>;
  estimate(bookId: string): Promise<ApiResult<Estimate>>;
  /** Fix a ready book: the plan with `dryRun`, else the change itself (API_CHANGES 16). */
  reviseChapters(
    bookId: string,
    chapters: ChapterPut[],
    dryRun: boolean,
  ): Promise<ApiResult<RevisePlan & { book?: Book }>>;
  confirm(bookId: string): Promise<ApiResult<Book>>;
  topUp(bookId: string): Promise<ApiResult<Book>>;
  billingMe(): Promise<ApiResult<Entitlement>>;
  checkout(plan: "starter" | "full", promo?: string | null): Promise<ApiResult<CheckoutHandshake>>;
  portal(): Promise<ApiResult<{ url: string }>>;
  redeem(code: string): Promise<ApiResult<Entitlement>>;
  getDeck(bookId: string): Promise<ApiResult<DeckDoc>>;
  getFindings(
    bookId: string,
  ): Promise<ApiResult<{ findings: FindingWire[]; summary?: CoverageSummary }>>;
  getProfile(subjectId: string): Promise<ApiResult<Profile>>;
  putProfile(subjectId: string, body: ProfilePut): Promise<ApiResult<Profile>>;
  confirmProfile(subjectId: string): Promise<ApiResult<Profile>>;
  relearnProfile(subjectId: string): Promise<ApiResult<Profile>>;
  requestPaper(subjectId: string, body: PaperRequest): Promise<ApiResult<PaperRow>>;
  listPapers(subjectId: string): Promise<ApiResult<{ papers: PaperRow[] }>>;
  getPaper(paperId: string): Promise<ApiResult<PaperRow>>;
  retryPaper(paperId: string): Promise<ApiResult<PaperRow>>;
  deletePaper(paperId: string): Promise<ApiResult<undefined>>;
  renamePaper(paperId: string, name: string | null): Promise<ApiResult<PaperRow>>;
  getPaperDoc(paperId: string): Promise<ApiResult<PaperDoc>>;
  sendFeedback(body: FeedbackBody): Promise<ApiResult<undefined>>;
};

export function makeApi(cfg: AppConfig, token: string): Api {
  return {
    me: () => apiFetch(cfg, token, "GET", "/identity/me"),
    acceptTerms: (version) => apiFetch(cfg, token, "POST", "/identity/terms", { version }),
    listSubjects: () => apiFetch(cfg, token, "GET", "/content/subjects"),
    createSubject: (name, qualification) =>
      apiFetch(cfg, token, "POST", "/content/subjects", { name, qualification }),
    listDocuments: (subjectId) =>
      apiFetch(cfg, token, "GET", `/content/documents?subject_id=${encodeURIComponent(subjectId)}`),
    createDocument: (input) => apiFetch(cfg, token, "POST", "/content/documents", input),
    listPastPapers: (id) => apiFetch(cfg, token, "GET", `/content/subjects/${id}/past-papers`),
    placeDocument: (id, body) =>
      apiFetch(cfg, token, "PUT", `/content/documents/${id}/placement`, body),
    renamePastPaper: (id, name) =>
      apiFetch(cfg, token, "PUT", `/content/past-papers/${id}/name`, { name }),
    deletePastPaper: (id) => apiFetch(cfg, token, "DELETE", `/content/past-papers/${id}`),
    getDocument: (id) => apiFetch(cfg, token, "GET", `/content/documents/${id}`),
    deleteDocument: (id) => apiFetch(cfg, token, "DELETE", `/content/documents/${id}`),
    deleteSubject: (id) => apiFetch(cfg, token, "DELETE", `/content/subjects/${id}`),
    getBook: (id) => apiFetch(cfg, token, "GET", `/content/books/${id}`),
    listChapters: (bookId) => apiFetch(cfg, token, "GET", `/content/books/${bookId}/chapters`),
    reviseChapters: (bookId, chapters, dryRun) =>
      apiFetch(cfg, token, "POST", `/content/books/${bookId}/revise`, {
        chapters,
        dry_run: dryRun,
      }),
    putChapters: (bookId, chapters) =>
      apiFetch(cfg, token, "PUT", `/content/books/${bookId}/chapters`, { chapters }),
    getExcerpt: (documentId, page) =>
      apiFetch(cfg, token, "GET", `/content/documents/${documentId}/pages/${page}/excerpt`),
    getPreview: (documentId, page) =>
      apiFetch(cfg, token, "GET", `/content/documents/${documentId}/pages/${page}/preview`),
    estimate: (bookId) => apiFetch(cfg, token, "POST", `/content/books/${bookId}/estimate`),
    confirm: (bookId) => apiFetch(cfg, token, "POST", `/content/books/${bookId}/confirm`),
    topUp: (bookId) => apiFetch(cfg, token, "POST", `/content/books/${bookId}/top-up`),
    billingMe: () => apiFetch(cfg, token, "GET", "/billing/me"),
    checkout: (plan, promo) =>
      apiFetch(cfg, token, "POST", "/billing/checkout", { plan, promo: promo ?? null }),
    portal: () => apiFetch(cfg, token, "POST", "/billing/portal"),
    redeem: (code) => apiFetch(cfg, token, "POST", "/billing/redeem", { code }),
    getDeck: (bookId) => apiFetch(cfg, token, "GET", `/generation/decks/${bookId}`),
    getFindings: (bookId) => apiFetch(cfg, token, "GET", `/content/books/${bookId}/findings`),
    getProfile: (id) => apiFetch(cfg, token, "GET", `/content/subjects/${id}/profile`),
    putProfile: (id, body) => apiFetch(cfg, token, "PUT", `/content/subjects/${id}/profile`, body),
    confirmProfile: (id) => apiFetch(cfg, token, "POST", `/content/subjects/${id}/profile/confirm`),
    relearnProfile: (id) => apiFetch(cfg, token, "POST", `/content/subjects/${id}/profile/relearn`),
    requestPaper: (id, body) =>
      apiFetch(cfg, token, "POST", `/content/subjects/${id}/papers`, body),
    listPapers: (id) => apiFetch(cfg, token, "GET", `/content/subjects/${id}/papers`),
    getPaper: (id) => apiFetch(cfg, token, "GET", `/content/papers/${id}`),
    retryPaper: (id) => apiFetch(cfg, token, "POST", `/content/papers/${id}/retry`),
    deletePaper: (id) => apiFetch(cfg, token, "DELETE", `/content/papers/${id}`),
    renamePaper: (id, name) => apiFetch(cfg, token, "PUT", `/content/papers/${id}/name`, { name }),
    getPaperDoc: (id) => apiFetch(cfg, token, "GET", `/generation/papers/${id}`),
    sendFeedback: (body) => apiFetch(cfg, token, "POST", "/content/feedback", body),
  };
}
